// filepath: apps/web/src/admin/tenants/TenantList.tsx
//
// FR-TEN-6 / ISS-050: operator tenant list with a de-provision action
// button per row. The list is rendered behind <TenantAdminGuard>;
// rows are loaded via a simple fetch (no React Query wiring in v1 —
// the list is admin-only and low-frequency).
//
// On click of "De-provision" the parent receives the selected
// tenant and renders <DeprovisionDialog /> with the irreversibility
// confirmation gate. The dialog is responsible for the typed-slug
// gate; the list page is just the entry point.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";

export interface TenantListItem {
  id: string;
  slug: string;
  name?: string;
  // ISS-509 (BA ruling): empty string is a valid tier value
  // (decoder fallback when the wire omits `tier`). The UI renders it
  // as a neutral blank — never invent a tier the server didn't send.
  tier: "" | "small" | "middle" | "corporate";
  // ISS-509 (BA ruling): "unknown" is a valid status value (decoder
  // fallback when the wire omits `status`). The UI renders the
  // localized "Unknown" label via
  // `t("operator.tenants.list.status.unknown", "Unknown")`.
  status: "active" | "disabled" | "quiesced" | "unknown";
}

export interface TenantListProps {
  tenants: TenantListItem[];
  isLoading: boolean;
  error?: string;
  onDeprovision: (tenant: TenantListItem) => void;
  onRefresh: () => void;
}

export function TenantList({
  tenants,
  isLoading,
  error,
  onDeprovision,
  onRefresh,
}: TenantListProps): React.ReactElement {
  const { t } = useTranslation();
  if (isLoading) {
    return <div data-testid="tenant-list-loading">Loading tenants…</div>;
  }
  if (error) {
    return (
      <div role="alert" data-testid="tenant-list-error">
        {t("operator.tenants.list.errorPrefix", "Failed to load tenants:")}{" "}
        {error}
        <button type="button" onClick={onRefresh}>
          {t("operator.tenants.list.refresh", "Refresh")}
        </button>
      </div>
    );
  }
  return (
    <section data-testid="tenant-list" className="tenant-list">
      <header>
        <h2 id="tenant-list-title">
          {t("operator.tenants.list.title", "Tenants")}
        </h2>
        <button
          type="button"
          onClick={onRefresh}
          data-testid="tenant-list-refresh"
        >
          {t("operator.tenants.list.refresh", "Refresh")}
        </button>
      </header>
      {tenants.length === 0 ? (
        <p>{t("operator.tenants.list.empty", "No tenants found.")}</p>
      ) : (
        <table aria-labelledby="tenant-list-title">
          <caption className="visually-hidden">
            {t(
              "operator.tenants.list.ariaLabel",
              "List of tenants with de-provisioning actions",
            )}
          </caption>
          <thead>
            <tr>
              <th>{t("operator.tenants.list.col.slug", "Slug")}</th>
              <th>{t("operator.tenants.list.col.name", "Name")}</th>
              <th>{t("operator.tenants.list.col.tier", "Tier")}</th>
              <th>{t("operator.tenants.list.col.status", "Status")}</th>
              <th>{t("operator.tenants.list.col.tenantId", "Tenant ID")}</th>
              <th>{t("operator.tenants.list.col.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((row) => (
              <tr key={row.id} data-testid={`tenant-row-${row.slug}`}>
                <td>
                  <code>{row.slug}</code>
                </td>
                <td>{row.name || "—"}</td>
                <td>
                  {/* ISS-509 / ISS-510: empty tier (fallback) →
                      neutral blank; a real tier value renders its
                      localized label (e.g. "Small" in en, "Малый"
                      in ru). The internal machine-readable value
                      ("" | "small" | "middle" | "corporate") never
                      leaks into the rendered DOM. */}
                  {row.tier === ""
                    ? ""
                    : t(`operator.tenants.list.tier.${row.tier}`, row.tier)}
                </td>
                <td>
                  {/* ISS-509 / ISS-510: "unknown" status (fallback) →
                      localized "Unknown" label; "active" / "disabled" /
                      "quiesced" also render their localized label, not
                      the raw internal value. */}
                  {t(`operator.tenants.list.status.${row.status}`, row.status)}
                </td>
                <td>
                  {/* ISS-232: tenant_id is the alternate-UUID canonical
                      identifier (per BA ruling on the alternate-UUID
                      convention). It is rendered visibly in a `<code>`
                      cell with a stable test-id so the failing-first
                      regression test can pin it. */}
                  <code
                    data-testid={`tenant-id-${row.slug}`}
                    style={{ fontSize: "var(--text-xs)" }} // design-adherence-ignore: code-cell monospace; var(--text-xs) is the project token
                  >
                    {row.id}
                  </code>
                </td>
                <td>
                  <button
                    type="button"
                    onClick={() => onDeprovision(row)}
                    data-testid={`deprovision-button-${row.slug}`}
                    aria-label={`${t("operator.tenants.list.deprovision", "De-provision")} tenant ${row.slug}`}
                  >
                    {t("operator.tenants.list.deprovision", "De-provision")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
